import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BookMarked,
  ChevronDown,
  ArrowLeftRight,
  Flag,
  GripVertical,
  Layers,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trash2,
  Upload,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const SUBJECT_FALLBACK = ["math", "science", "english", "hindi"];
const SUBJECT_LABELS = { english: "English Grammar", hindi: "Hindi Vyakaran" };
const subjectLabel = (s) => SUBJECT_LABELS[(s || "").toLowerCase()] || ((s || "").charAt(0).toUpperCase() + (s || "").slice(1));
const DIFF_COLORS = {
  easy: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  hard: "bg-red-100 text-red-700",
};

// Options may be stored as an array OR a legacy dict {A:..,B:..}; always work with an array.
const optionsToArray = (opts) => {
  if (Array.isArray(opts)) return opts;
  if (opts && typeof opts === "object") return Object.values(opts);
  return [];
};

const req = async (url, opts = {}) => {
  const res = await fetch(`${API}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
};

export default function ContentEditorTab({ isContentManager = false, scope = null, readOnly = false }) {
  const scopedGrades = isContentManager && scope?.grades?.length ? scope.grades.map(String) : ["5", "6", "7", "8"];
  const scopedSubjects = isContentManager && scope?.subjects?.length ? scope.subjects.map((s) => s.toLowerCase()) : null;
  const [board, setBoard] = useState("cbse");
  const [grade, setGrade] = useState(scopedGrades[0] || "6");
  const [subject, setSubject] = useState((scopedSubjects && scopedSubjects[0]) || "math");
  const [subjects, setSubjects] = useState(scopedSubjects || SUBJECT_FALLBACK);
  const [flagFor, setFlagFor] = useState(null); // topic to flag as duplicate
  const [flagScFor, setFlagScFor] = useState(null); // {topic, cd} sub-concept to flag
  const [textbookFor, setTextbookFor] = useState(null); // topic to manage textbook
  const [showBulkTextbook, setShowBulkTextbook] = useState(false);
  const [mcqBusy, setMcqBusy] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState({}); // key -> {job_id, generated, target, status}
  const [editSC, setEditSC] = useState(null); // {topic, cd}
  const [editQ, setEditQ] = useState(null); // {question}
  const [newTopic, setNewTopic] = useState("");
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [addSubFor, setAddSubFor] = useState(null); // topic
  const [renameTopicFor, setRenameTopicFor] = useState(null); // topic to rename
  const [fillJob, setFillJob] = useState(null); // {job_id/id, filled, total, status}
  const [mcqJob, setMcqJob] = useState(null); // bulk MCQ convert job
  const [dupInfo, setDupInfo] = useState(null);
  const [showDupModal, setShowDupModal] = useState(false);
  const [cleaningDups, setCleaningDups] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [publishing, setPublishing] = useState(false);
  const [publishJob, setPublishJob] = useState(null);
  const publishPoll = useRef(null);
  const [mirroring, setMirroring] = useState(false);
  const [mirrorJob, setMirrorJob] = useState(null);
  const mirrorPoll = useRef(null);

  useEffect(() => () => {
    if (publishPoll.current) clearInterval(publishPoll.current);
    if (mirrorPoll.current) clearInterval(mirrorPoll.current);
  }, []);

  const mirrorToProd = async () => {
    const contentScope = { board, grade, subject };
    const label = `${board.toUpperCase()} · Class ${grade} · ${subjectLabel(subject)}`;
    setMirroring(true);
    setMirrorJob(null);
    try {
      const prev = await req(`/admin/migration/content-mirror/preview`, {
        method: "POST", body: JSON.stringify({ content_scope: contentScope }),
      });
      const d = prev.deletions;
      const typed = window.prompt(
        `MIRROR "${label}" to PRODUCTION?\n\n` +
        `This DELETES ${prev.total_deletions} prod record(s) that aren't in preview:\n` +
        `• topics: ${d.topics.count}${d.topics.sample?.length ? " (e.g. " + d.topics.sample.slice(0, 5).join(", ") + ")" : ""}\n` +
        `• questions: ${d.questions.count}\n• textbooks: ${d.textbooks.count}\n\n` +
        `Then re-pushes preview: ${prev.upserts.topics} topics / ${prev.upserts.questions} questions / ${prev.upserts.textbooks} textbooks.\n\n` +
        `Student/user data is never touched.\n\nType MIRROR to confirm:`);
      if (typed !== "MIRROR") {
        setMirroring(false);
        if (typed !== null) toast.info("Mirror cancelled");
        return;
      }
      const res = await req(`/admin/migration/content-mirror/execute`, {
        method: "POST", body: JSON.stringify({ content_scope: contentScope, confirm: "MIRROR" }),
      });
      setMirrorJob({ status: "running" });
      toast.info(`Mirroring "${label}" to Prod…`);
      mirrorPoll.current = setInterval(async () => {
        try {
          const st = await req(`/admin/migration/status/${res.job_id}`);
          setMirrorJob(st.job);
          if (st.job.status === "completed" || st.job.status === "failed") {
            clearInterval(mirrorPoll.current);
            setMirroring(false);
            if (st.job.status === "completed") toast.success(`Mirrored "${label}" — prod now matches preview`);
            else toast.error("Mirror failed: " + (st.job.error || "unknown"));
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Mirror failed");
      setMirroring(false);
    }
  };

  const publishToProd = async () => {
    const contentScope = { board, grade, subject };
    const label = `${board.toUpperCase()} · Class ${grade} · ${subjectLabel(subject)}`;
    setPublishing(true);
    setPublishJob(null);
    try {
      const diff = await req(`/admin/migration/diff`, {
        method: "POST", body: JSON.stringify({ direction: "push", content_scope: contentScope }),
      });
      const rows = diff.rows || [];
      const totalNew = rows.reduce((s, r) => s + (r.missing_count || 0), 0);
      const summary = rows.map((r) => `• ${r.collection}: ${r.missing_count || 0} new (source ${r.source_count}, prod ${r.target_count})`).join("\n");
      if (!window.confirm(`Publish "${label}" to PRODUCTION?\n\n${summary}\n\nMerge/Upsert — adds new & updates changed content, never deletes.\n${totalNew === 0 ? "\nNote: nothing is new, but any edited records will still be updated." : ""}`)) {
        setPublishing(false);
        return;
      }
      const res = await req(`/admin/migration/start`, {
        method: "POST", body: JSON.stringify({ direction: "push", content_scope: contentScope, strategy: "merge" }),
      });
      setPublishJob({ status: "running", processed: 0, total: 0 });
      toast.info(`Publishing "${label}" to Prod…`);
      publishPoll.current = setInterval(async () => {
        try {
          const st = await req(`/admin/migration/status/${res.job_id}`);
          setPublishJob(st.job);
          if (st.job.status === "completed" || st.job.status === "failed") {
            clearInterval(publishPoll.current);
            setPublishing(false);
            if (st.job.status === "completed") toast.success(`Published "${label}" to Prod ✔`);
            else toast.error("Publish failed: " + (st.job.error || "unknown"));
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Publish failed");
      setPublishing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const d = await req(`/admin/curriculum/content/models?task=lesson`);
        setModels(d.models || []);
        if (d.default) setModel(d.default);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const loadTopics = useCallback(async () => {
    setLoading(true);
    setSelectedTopics(new Set());
    try {
      const params = new URLSearchParams({ board, grade, subject });
      const data = await req(`/admin/curriculum/content/detail?${params}`);
      setTopics(data.topics || []);
      if (!scopedSubjects && data.subjects?.length) setSubjects(data.subjects);
    } catch (e) {
      toast.error(e.message || "Could not load content");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [board, grade, subject]);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  // Poll the fill-missing-lessons job
  useEffect(() => {
    if (!fillJob || fillJob.status !== "running") return;
    const jobId = fillJob.id;
    const timer = setInterval(async () => {
      try {
        const data = await req(`/admin/curriculum/content/fill-missing-lessons/${jobId}`);
        setFillJob((prev) => ({ ...prev, ...data.job }));
        if (data.job.status === "done") {
          toast.success(`Filled ${data.job.filled} missing lesson(s)`);
          loadTopics();
        } else if (data.job.status === "failed") {
          toast.error(`Fill failed: ${data.job.error || "unknown error"}`);
        }
      } catch {
        /* ignore transient poll errors */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [fillJob, loadTopics]);

  // Poll the bulk MCQ-convert job
  useEffect(() => {
    if (!mcqJob || mcqJob.status !== "running") return;
    const jobId = mcqJob.id;
    const timer = setInterval(async () => {
      try {
        const data = await req(`/admin/curriculum/content/convert-mcq-bulk/${jobId}`);
        setMcqJob((prev) => ({ ...prev, ...data.job }));
        if (data.job.status === "done") {
          toast.success(`Converted ${data.job.converted} question(s) to MCQ`);
          loadTopics();
        } else if (data.job.status === "failed") {
          toast.error(`MCQ conversion failed: ${data.job.error || "unknown error"}`);
        }
      } catch {
        /* ignore transient poll errors */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [mcqJob, loadTopics]);

  const loadDuplicates = useCallback(async () => {
    try {
      const data = await req(`/admin/curriculum/duplicates`);
      setDupInfo(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadDuplicates();
  }, [loadDuplicates]);

  const cleanupDuplicates = async () => {
    setCleaningDups(true);
    try {
      const remove_ids = (dupInfo?.groups || []).flatMap((g) => g.remove.map((r) => r._id));
      const data = await req(`/admin/curriculum/duplicates/cleanup`, {
        method: "POST",
        body: JSON.stringify({ remove_ids }),
      });
      toast.success(`Removed ${data.deleted} duplicate topic(s)`);
      setShowDupModal(false);
      loadDuplicates();
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Cleanup failed");
    } finally {
      setCleaningDups(false);
    }
  };

  const fillMissingLessons = async () => {
    try {
      const data = await req(`/admin/curriculum/content/fill-missing-lessons`, {
        method: "POST",
        body: JSON.stringify({ model }),
      });
      if (data.job.total === 0) {
        toast.success("Every topic already has a static lesson 🎉");
        return;
      }
      setFillJob(data.job);
      toast.info(`Generating ${data.job.total} missing lesson(s) in the background…`);
    } catch (e) {
      toast.error(e.message || "Could not start fill");
    }
  };

  const convertAllMcq = async () => {
    try {
      const data = await req(`/admin/curriculum/content/convert-mcq-bulk`, {
        method: "POST",
        body: JSON.stringify({ model }),
      });
      if (data.job.total === 0) {
        toast.success("Everything is already MCQ 🎉");
        return;
      }
      setMcqJob(data.job);
      toast.info(`Converting ${data.job.total} question(s) to MCQ in the background…`);
    } catch (e) {
      toast.error(e.message || "Could not start MCQ conversion");
    }
  };

  const createTopic = async () => {
    const name = newTopic.trim();
    if (!name) return;
    setCreatingTopic(true);
    try {
      await req(`/admin/curriculum/topic/create`, {
        method: "POST",
        body: JSON.stringify({ board, grade, subject, topic: name }),
      });
      toast.success(`Topic "${name}" created`);
      setNewTopic("");
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Could not create topic");
    } finally {
      setCreatingTopic(false);
    }
  };

  const reorderSubconcepts = async (topic, orderedIds) => {
    // optimistic local update
    setTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topic.id) return t;
        const byId = Object.fromEntries((t.concept_details || []).map((c) => [c.id, c]));
        return { ...t, concept_details: orderedIds.map((id) => byId[id]).filter(Boolean) };
      })
    );
    try {
      await req(`/admin/curriculum/topic/${topic.id}/reorder`, {
        method: "PUT",
        body: JSON.stringify({ order: orderedIds }),
      });
      toast.success("Sub-concept order saved");
    } catch (e) {
      toast.error(e.message || "Reorder failed");
      loadTopics();
    }
  };

  const deleteTopic = async (topic) => {
    if (!window.confirm(`Delete topic "${topic.topic}" and ALL its questions?`)) return;
    try {
      const data = await req(`/admin/curriculum/topic/${topic.id}`, { method: "DELETE" });
      toast.success(`Topic deleted (${data.deleted_questions} questions removed)`);
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const toggleTopicSelect = (id) =>
    setSelectedTopics((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const bulkDeleteTopics = async () => {
    const ids = [...selectedTopics];
    if (!ids.length) return;
    const names = topics.filter((t) => ids.includes(t.id)).map((t) => t.topic);
    const confirmText = window.prompt(
      `Permanently delete ${ids.length} topic(s) and ALL their sub-topics & questions?\n\n` +
        names.map((n) => `• ${n}`).join("\n") +
        `\n\nThis CANNOT be undone. Type DELETE to confirm.`
    );
    if (confirmText !== "DELETE") {
      if (confirmText !== null) toast.info("Deletion cancelled — type DELETE exactly to confirm.");
      return;
    }
    setBulkDeleting(true);
    try {
      const data = await req(`/admin/curriculum/topics/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ topic_ids: ids }),
      });
      toast.success(`Deleted ${data.deleted_topics} topic(s) and ${data.deleted_questions} question(s)`);
      setSelectedTopics(new Set());
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const convertTopicMcq = async (topic) => {
    if (!window.confirm(`Convert "${topic.topic}" concept-checks & practice questions to multiple-choice? This may take a moment.`)) return;
    setMcqBusy(topic.id);
    try {
      const data = await req(`/admin/curriculum/topic/${topic.id}/convert-mcq`, {
        method: "POST", body: JSON.stringify({ model }),
      });
      toast.success(`Converted ${data.concept_checks_converted} concept-checks & ${data.practice_converted} practice questions to MCQ`);
    } catch (e) {
      toast.error(e.message || "Conversion failed (it may still be running)");
    } finally {
      setMcqBusy(null);
    }
  };


  const deleteSubconcept = async (topic, sc) => {
    if (!window.confirm(`Delete sub-concept "${sc.title}" and its questions?`)) return;
    try {
      await req(`/admin/curriculum/subconcept/${topic.id}/${sc.id}`, { method: "DELETE" });
      toast.success("Sub-concept deleted");
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const regenSubconcept = async (topic, sc) => {
    const key = `regen:${topic.id}:${sc.id}`;
    setJobs((p) => ({ ...p, [key]: { status: "running" } }));
    try {
      await req(`/admin/curriculum/subconcept/regenerate`, {
        method: "POST",
        body: JSON.stringify({ topic_id: topic.id, subconcept_id: sc.id, model }),
      });
      toast.success(`Regenerated lesson for "${sc.title}"`);
      loadTopics();
    } catch (e) {
      toast.error(e.message || "Regeneration failed");
    } finally {
      setJobs((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="content-editor-tab">
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Content Editor</h3>
              <p className="text-sm text-slate-600">
                {readOnly
                  ? "Viewing live lessons and practice questions. Content is authored in Preview and promoted here — editing is disabled in Production."
                  : isContentManager
                  ? "Browse, edit and AI-regenerate lessons and practice questions within your assigned classes and subjects. Changes are live."
                  : "Browse, edit, AI-regenerate and delete live lessons and practice questions. All changes are live."}
              </p>
            </div>
            {fillJob?.status === "running" || mcqJob?.status === "running" ? (
              <div className="flex items-center gap-2 shrink-0">
                {fillJob?.status === "running" && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Filling {fillJob.filled || 0}/{fillJob.total}
                  </Badge>
                )}
                {mcqJob?.status === "running" && (
                  <Badge className="bg-emerald-100 text-emerald-700" data-testid="mcq-bulk-progress">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Converting {mcqJob.converted || 0}/{mcqJob.total}
                  </Badge>
                )}
              </div>
            ) : readOnly ? null : (
              <div className="flex items-center gap-2 shrink-0">
                {!isContentManager && dupInfo?.total_removable > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowDupModal(true)}
                    data-testid="cleanup-dups-btn"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    title="Review and remove duplicate topic entries"
                  >
                    <Layers className="h-4 w-4 mr-1" />
                    Clean up duplicates ({dupInfo.total_removable})
                  </Button>
                )}
                {!isContentManager && (
                  <Button
                    variant="outline"
                    onClick={convertAllMcq}
                    data-testid="convert-mcq-bulk-btn"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    title="Convert every Math, Science & English quick-check and practice question (all classes) to multiple-choice"
                  >
                    <ListChecks className="h-4 w-4 mr-1" />
                    Convert all to MCQ
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowBulkTextbook(true)}
                  data-testid="bulk-textbook-btn"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  title="Upload multiple chapter PDFs at once and auto-build each to its book flow"
                >
                  <BookMarked className="h-4 w-4 mr-1" />
                  Bulk textbooks
                </Button>
                <Button
                  variant="outline"
                  onClick={fillMissingLessons}
                  data-testid="fill-missing-lessons-btn"
                  title="Generate static lessons for every sub-concept that is still missing one"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Fill missing lessons
                </Button>
              </div>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={board}
              onChange={(e) => setBoard(e.target.value)}
              data-testid="content-board-select"
            >
              <option value="cbse">CBSE</option>
              <option value="ib">IB</option>
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              data-testid="content-grade-select"
            >
              {scopedGrades.map((g) => (
                <option key={g} value={g}>
                  Class {g}
                </option>
              ))}
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="content-subject-select"
            >
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {subjectLabel(s)}
                </option>
              ))}
            </select>
          </div>
          {!readOnly && (
          <div className="flex flex-wrap items-end gap-2 pt-1 border-t mt-1">
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-slate-500 mb-1">AI model for generation</label>
              <select
                className="h-10 rounded-md border border-input bg-background px-3 min-w-[220px]"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                data-testid="content-model-select"
              >
                {models.length === 0 && <option value={model}>{model}</option>}
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 flex gap-2 min-w-[260px]">
              <Input
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createTopic()}
                placeholder={`Add a new topic to ${subject} · Class ${grade}…`}
                data-testid="add-topic-input"
              />
              <Button onClick={createTopic} disabled={creatingTopic} data-testid="add-topic-btn">
                {creatingTopic ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Add Topic
              </Button>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {!isContentManager && selectedTopics.size > 0 && (
        <div
          className="sticky top-2 z-30 flex items-center justify-between gap-3 bg-red-600 text-white rounded-lg px-4 py-2 shadow-lg"
          data-testid="topic-bulk-bar"
        >
          <span className="text-sm font-medium" data-testid="topic-bulk-count">
            {selectedTopics.size} topic{selectedTopics.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSelectedTopics(new Set(topics.map((t) => t.id)))}
              data-testid="topic-select-all-btn"
            >
              Select all ({topics.length})
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-red-500"
              onClick={() => setSelectedTopics(new Set())}
              data-testid="topic-clear-selection-btn"
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="bg-white text-red-600 hover:bg-red-50"
              onClick={bulkDeleteTopics}
              disabled={bulkDeleting}
              data-testid="topic-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading content…
        </div>
      ) : topics.length === 0 ? (
        <p className="text-sm text-slate-500">
          No topics found for {board.toUpperCase()} · Class {grade} · {subject}.
        </p>
      ) : (
        <Accordion type="multiple" className="space-y-3">
          {topics.map((topic) => {
            return (
              <AccordionItem
                key={topic.id}
                value={topic.id}
                className="border rounded-lg bg-white px-4"
                data-testid={`topic-row-${topic.id}`}
              >
                <div className="flex items-center justify-between gap-3 py-1">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {!isContentManager && (
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-red-600 cursor-pointer shrink-0"
                        checked={selectedTopics.has(topic.id)}
                        onChange={() => toggleTopicSelect(topic.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`topic-select-${topic.id}`}
                        title="Select for bulk delete"
                      />
                    )}
                    <AccordionTrigger className="flex-1 hover:no-underline">
                      <div className="flex items-center gap-2 text-left">
                        <ChevronDown className="hidden" />
                        <span className="font-semibold">{topic.topic}</span>
                        <Badge variant="secondary">
                          {topic.concept_details?.length || topic.concepts?.length || 0} sub-concepts
                        </Badge>
                        {topic.status && topic.status !== "approved" && (
                          <Badge variant="outline">{topic.status}</Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isContentManager && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-600 hover:text-slate-800"
                        onClick={() => setRenameTopicFor(topic)}
                        data-testid={`rename-topic-${topic.id}`}
                        title="Rename this topic"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className={topic.textbook
                        ? "text-emerald-700 hover:text-emerald-800 max-w-[200px]"
                        : "text-slate-400 hover:text-indigo-600"}
                      onClick={() => setTextbookFor(topic)}
                      data-testid={`textbook-topic-${topic.id}`}
                      title={topic.textbook
                        ? `Textbook: ${topic.textbook.filename}${topic.textbook.page_count ? ` (${topic.textbook.page_count} pages)` : ""} — click to manage or replace`
                        : "No textbook uploaded — click to upload a PDF"}
                    >
                      <BookMarked className="h-4 w-4 shrink-0" />
                      <span
                        className="ml-1 text-xs truncate hidden sm:inline"
                        data-testid={`textbook-name-${topic.id}`}
                      >
                        {topic.textbook ? topic.textbook.filename : "No PDF"}
                      </span>
                    </Button>
                    {!isContentManager && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:text-emerald-700"
                        onClick={() => convertTopicMcq(topic)}
                        disabled={mcqBusy === topic.id}
                        data-testid={`convert-mcq-${topic.id}`}
                        title="Convert this topic's concept-checks & practice questions to multiple-choice"
                      >
                        {mcqBusy === topic.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
                      </Button>
                    )}
                    {isContentManager ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-amber-600 hover:text-amber-700"
                        onClick={() => setFlagFor(topic)}
                        data-testid={`flag-topic-${topic.id}`}
                        title="Flag this topic as a duplicate for admin review"
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteTopic(topic)}
                        data-testid={`del-topic-${topic.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <AccordionContent className="pb-4">
                  <SubconceptList
                    topic={topic}
                    jobs={jobs}
                    isContentManager={isContentManager}
                    onEdit={(cd) => setEditSC({ topic, cd })}
                    onRegen={(cd) => regenSubconcept(topic, cd)}
                    onDelete={(cd) => deleteSubconcept(topic, cd)}
                    onFlag={(cd) => setFlagScFor({ topic, cd })}
                    onReorder={(orderedIds) => reorderSubconcepts(topic, orderedIds)}
                    onAddSub={() => setAddSubFor(topic)}
                  />
                  <QuestionList topicId={topic.id} model={model} onEditQ={(q) => setEditQ({ question: q })} />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {editSC && (
        <SubconceptEditModal
          data={editSC}
          onClose={() => setEditSC(null)}
          onSaved={() => {
            setEditSC(null);
            loadTopics();
          }}
        />
      )}
      {editQ && (
        <QuestionEditModal
          question={editQ.question}
          onClose={() => setEditQ(null)}
          onSaved={() => {
            setEditQ(null);
          }}
        />
      )}
      {addSubFor && (
        <AddSubtopicModal
          topic={addSubFor}
          model={model}
          onClose={() => setAddSubFor(null)}
          onSaved={() => {
            setAddSubFor(null);
            loadTopics();
          }}
        />
      )}
      {renameTopicFor && (
        <RenameTopicModal
          topic={renameTopicFor}
          onClose={() => setRenameTopicFor(null)}
          onSaved={() => {
            setRenameTopicFor(null);
            loadTopics();
          }}
        />
      )}
      {flagFor && (
        <FlagDuplicateModal
          topic={flagFor}
          candidates={topics.filter((t) => t.id !== flagFor.id)}
          onClose={() => setFlagFor(null)}
          onFlagged={() => setFlagFor(null)}
        />
      )}
      {flagScFor && (
        <FlagSubconceptModal
          source={flagScFor}
          topics={topics}
          onClose={() => setFlagScFor(null)}
          onFlagged={() => setFlagScFor(null)}
        />
      )}
      {textbookFor && (
        <TextbookModal
          topic={textbookFor}
          model={model}
          isContentManager={isContentManager}
          onClose={() => { setTextbookFor(null); loadTopics(); }}
          onSynced={() => { setTextbookFor(null); loadTopics(); }}
        />
      )}
      {showBulkTextbook && (
        <BulkTextbookModal
          topics={topics}
          model={model}
          onClose={() => setShowBulkTextbook(false)}
          onDone={() => { setShowBulkTextbook(false); loadTopics(); }}
        />
      )}
      {showDupModal && dupInfo && (
        <Dialog open onOpenChange={() => setShowDupModal(false)}>
          <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dup-modal">
            <DialogTitle className="text-lg font-semibold">Clean up duplicate topics</DialogTitle>
            <p className="text-sm text-slate-600">
              These topics exist more than once. We'll keep the best copy (proper title
              & content) and remove the duplicate. Questions are shared and stay intact.
            </p>
            <div className="space-y-3">
              {(dupInfo.groups || []).map((g) => (
                <div key={g.id} className="border rounded-md p-3" data-testid={`dup-group-${g.id}`}>
                  <p className="text-xs font-mono text-slate-400 mb-2">{g.id}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="bg-emerald-100 text-emerald-700">KEEP</Badge>
                    <span className="font-medium">{g.keep.topic}</span>
                    <span className="text-xs text-slate-500">
                      ({g.keep.source} · {g.keep.filled_lessons}/{g.keep.subconcepts} lessons)
                    </span>
                  </div>
                  {g.remove.map((r) => (
                    <div key={r._id} className="flex items-center gap-2 text-sm mt-1">
                      <Badge className="bg-red-100 text-red-700">REMOVE</Badge>
                      <span className="line-through text-slate-500">{r.topic}</span>
                      <span className="text-xs text-slate-400">({r.source})</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDupModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={cleanupDuplicates}
                disabled={cleaningDups}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="dup-confirm-btn"
              >
                {cleaningDups && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Remove {dupInfo.total_removable} duplicate(s)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function BulkTextbookModal({ topics, model, onClose, onDone }) {
  const [rows, setRows] = useState([]); // {file, topicId, status}
  const [running, setRunning] = useState(false);
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const onPick = (files) => {
    const fl = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const mapped = fl.map((file) => {
      // best-effort auto-match filename to a topic
      const base = file.name.replace(/\.pdf$/i, "").toLowerCase();
      const match = topics.find((t) => base.includes(t.topic.toLowerCase()) || t.topic.toLowerCase().includes(base.split(/[^a-z]+/)[0] || "###"));
      return { file, topicId: match?.id || "", status: "pending" };
    });
    // Append (dedupe by name+size) so users can add more files across multiple picks.
    setRows((prev) => {
      const seen = new Set(prev.map((r) => `${r.file.name}:${r.file.size}`));
      return [...prev, ...mapped.filter((m) => !seen.has(`${m.file.name}:${m.file.size}`))];
    });
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const replaceFile = (i, files) => {
    const f = Array.from(files || []).find((x) => x.name.toLowerCase().endsWith(".pdf"));
    if (f) setRow(i, { file: f, status: "pending", result: undefined });
  };

  const pollRow = (topicId, jobId) => new Promise((resolve, reject) => {
    const tick = async () => {
      try {
        const d = await req(`/admin/curriculum/topic/${topicId}/textbook/rebuild-status/${jobId}`);
        const j = d.job || {};
        if (j.status === "completed") return resolve(j);
        if (j.status === "failed") return reject(new Error(j.error || "Rebuild failed"));
        setTimeout(tick, 2500);
      } catch (e) { reject(e); }
    };
    tick();
  });

  // Upload + build a single row (used by the parallel runner and the per-row Retry button).
  const runRow = async (i) => {
    const row = rowsRef.current[i];
    if (!row || !row.topicId) return;
    setRow(i, { status: "uploading", result: undefined });
    try {
      const fd = new FormData();
      fd.append("file", row.file);
      if (model) fd.append("model", model);
      const res = await fetch(`${API}/admin/curriculum/topic/${row.topicId}/textbook`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed");
      setRow(i, { status: "building" });
      const j = await pollRow(row.topicId, data.job_id);
      setRow(i, { status: "done", result: `${data.pages}p · ${j.total_subconcepts || 0} sub-topics (book order)` });
    } catch (e) {
      setRow(i, { status: "error", result: e.message || "Failed" });
    }
  };

  // Run selected rows in parallel with a small concurrency cap. Kept at 2 so a few large PDFs
  // don't saturate the server's CPU during text extraction (which previously caused gateway errors).
  const runPool = async (indices, limit = 2) => {
    let cursor = 0;
    const workers = Array.from({ length: Math.min(limit, indices.length) }, async () => {
      while (cursor < indices.length) {
        const my = indices[cursor++];
        await runRow(my);
      }
    });
    await Promise.all(workers);
  };

  const runAll = async () => {
    if (rows.some((r) => !r.topicId)) return toast.error("Assign a topic to every PDF first");
    const indices = rows.map((r, i) => i).filter((i) => rows[i].status !== "done");
    if (indices.length === 0) return;
    setRunning(true);
    await runPool(indices);
    setRunning(false);
    toast.success("Bulk textbook build complete");
  };

  const retryRow = async (i) => {
    setRunning(true);
    await runRow(i);
    setRunning(false);
  };

  const topicName = (id) => topics.find((t) => t.id === id)?.topic || "";

  return (
    <Dialog open onOpenChange={running ? undefined : onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-textbook-modal">
        <DialogTitle className="text-lg font-semibold flex items-center gap-2">
          <BookMarked className="h-4 w-4 text-indigo-600" /> Bulk textbook upload
        </DialogTitle>
        <p className="text-sm text-slate-600">
          Upload several chapter PDFs for the selected class &amp; subject. Assign each to a topic —
          each book is indexed, lessons are generated from it, and sub-topics are ordered to the chapter.
        </p>

        <label className="block border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400" data-testid="bulk-textbook-input-label">
          <span className="flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Upload className="h-5 w-5" /> Click to choose PDF files (multiple)
          </span>
          <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => onPick(e.target.files)} data-testid="bulk-textbook-input" />
        </label>

        {rows.length > 0 && (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 border rounded-md p-2" data-testid={`bulk-row-${i}`}>
                <span className="text-sm font-medium flex-1 min-w-0 truncate">📄 {r.file.name}</span>
                <select
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={r.topicId}
                  onChange={(e) => setRow(i, { topicId: e.target.value })}
                  disabled={running}
                  data-testid={`bulk-row-topic-${i}`}
                >
                  <option value="">Assign topic…</option>
                  {topics.map((t) => (
                    <option key={t.id} value={t.id}>{t.topic}</option>
                  ))}
                </select>
                <span className="text-xs w-32 text-right">
                  {r.status === "pending" && <span className="text-slate-400">Ready</span>}
                  {r.status === "uploading" && <span className="text-blue-600 flex items-center justify-end gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Indexing…</span>}
                  {r.status === "building" && <span className="text-blue-600 flex items-center justify-end gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Building…</span>}
                  {r.status === "done" && <span className="text-emerald-600">✓ {r.result}</span>}
                  {r.status === "error" && <span className="text-red-600" title={r.result}>✗ {r.result}</span>}
                </span>
                <div className="flex items-center gap-1">
                  {r.status === "error" && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 px-2" disabled={running || !r.topicId}
                        onClick={() => retryRow(i)} data-testid={`bulk-row-retry-${i}`} title="Retry this PDF">
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                      </Button>
                      <label className="h-8 px-2 inline-flex items-center text-xs border rounded-md cursor-pointer hover:bg-slate-50" title="Re-upload a different PDF" data-testid={`bulk-row-replace-label-${i}`}>
                        <Upload className="h-3.5 w-3.5" />
                        <input type="file" accept="application/pdf" className="hidden" disabled={running}
                          onChange={(e) => replaceFile(i, e.target.files)} data-testid={`bulk-row-replace-${i}`} />
                      </label>
                    </>
                  )}
                  {!running && r.status !== "done" && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                      onClick={() => removeRow(i)} data-testid={`bulk-row-remove-${i}`} title="Remove">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <p className="text-[11px] text-slate-400">
            PDFs are processed in parallel (up to 4 at a time). Failed PDFs can be retried or re-uploaded individually.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={running}>{rows.some((r) => r.status === "done") ? "Close" : "Cancel"}</Button>
          <Button onClick={runAll} disabled={running || rows.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-textbook-run">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {rows.some((r) => r.status === "error") ? "Build remaining" : "Upload & build"} {rows.length ? `(${rows.filter((r) => r.status !== "done").length})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextbookModal({ topic, model, isContentManager, onClose, onSynced }) {
  const [tb, setTb] = useState(undefined); // undefined=loading, null=none
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fileName, setFileName] = useState("");
  const [phase, setPhase] = useState(""); // "", "indexing", "building"

  const loadStatus = async () => {
    try {
      const d = await req(`/admin/curriculum/topic/${topic.id}/textbook`);
      setTb(d.textbook || null);
    } catch {
      setTb(null);
    }
  };
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  // Poll a background index/rebuild job to completion.
  const pollBuild = (jobId) => new Promise((resolve, reject) => {
    const tick = async () => {
      try {
        const d = await req(`/admin/curriculum/topic/${topic.id}/textbook/rebuild-status/${jobId}`);
        const j = d.job || {};
        setPhase(j.status);
        if (j.status === "completed") return resolve(j);
        if (j.status === "failed") return reject(new Error(j.error || "Rebuild failed"));
        setTimeout(tick, 2000);
      } catch (e) {
        reject(e);
      }
    };
    tick();
  });

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose a PDF file");
    setUploading(true);
    setFileName(file.name);
    setPhase("indexing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (model) fd.append("model", model);
      const res = await fetch(`${API}/admin/curriculum/topic/${topic.id}/textbook`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      const j = await pollBuild(data.job_id);
      toast.success(`Indexed ${data.pages} pages · sub-concepts rebuilt to the book's order (${j.total_subconcepts || 0})`);
      onSynced(); // refresh list + close; the topic now mirrors the book's sub-topics in order
    } catch (e) {
      toast.error(e.message || "Upload failed");
      await loadStatus();
    } finally {
      setUploading(false);
      setPhase("");
    }
  };

  const confirmRebuild = async (op) => {
    try {
      const p = await req(`/admin/curriculum/topic/${topic.id}/textbook/rebuild-preview?op=${op}`);
      if ((p.dropped || []).length > 0) {
        const lines = p.dropped
          .map((d) => `• ${d.title} (${d.question_count} question${d.question_count === 1 ? "" : "s"})`)
          .join("\n");
        return window.confirm(
          `This will DROP ${p.dropped.length} sub-topic(s) and permanently delete ${p.total_questions_pruned} linked question(s):\n\n${lines}\n\n` +
            `${(p.added || []).length} new sub-topic(s) will be added. Continue?`
        );
      }
      return true;
    } catch {
      return true; // preview is best-effort; never block the action
    }
  };

  const sync = async () => {
    if (!(await confirmRebuild("sync"))) return;
    setSyncing(true);
    setPhase("building");
    try {
      const d = await req(`/admin/curriculum/topic/${topic.id}/textbook/sync-subconcepts`, {
        method: "POST", body: JSON.stringify({ model }),
      });
      const j = await pollBuild(d.job_id);
      toast.success(`Rebuilt ${j.total_subconcepts || 0} sub-topics from the book, in the book's order`);
      onSynced();
    } catch (e) {
      toast.error(e.message || "Rebuild failed");
    } finally {
      setSyncing(false);
      setPhase("");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the uploaded textbook for this topic?")) return;
    try {
      await req(`/admin/curriculum/topic/${topic.id}/textbook`, { method: "DELETE" });
      toast.success("Textbook removed");
      setTb(null);
    } catch (e) {
      toast.error(e.message || "Could not remove");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="textbook-modal">
        <DialogTitle className="text-lg font-semibold flex items-center gap-2">
          <BookMarked className="h-4 w-4 text-indigo-600" /> Textbook · {topic.topic}
        </DialogTitle>
        <p className="text-sm text-slate-600">
          Upload the chapter PDF. The topic's sub-concepts are rebuilt to mirror the book's own
          sub-topics, <strong>in the exact order they are printed</strong>, with each lesson grounded in
          the book's definitions, methods and examples — not generic knowledge.
        </p>

        {tb === undefined ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : tb ? (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm font-medium text-emerald-800">📘 {tb.filename}</p>
              <p className="text-xs text-emerald-700">{tb.page_count} pages · {tb.char_count?.toLocaleString()} chars · {(tb.sections || []).length} sub-topics indexed</p>
            </div>
            {(tb.sections || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Book sub-topics (in printed order)</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  {tb.sections.map((s, i) => (
                    <li key={i} className="text-xs text-slate-700">{s.title}</li>
                  ))}
                </ol>
              </div>
            )}
            <Button onClick={sync} disabled={syncing || uploading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="textbook-sync-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {syncing ? (phase === "indexing" ? "Indexing book…" : "Rebuilding in book order…") : "Rebuild sub-concepts from book (match book order)"}
            </Button>
            <div className="flex items-center justify-between gap-2 pt-1 border-t">
              <label className="text-sm text-indigo-600 hover:underline cursor-pointer">
                {uploading ? "Replacing…" : "Replace PDF"}
                <input type="file" accept="application/pdf" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} data-testid="textbook-replace-input" />
              </label>
              {!isContentManager && (
                <button onClick={remove} className="text-sm text-red-600 hover:underline" data-testid="textbook-remove-btn">Remove textbook</button>
              )}
            </div>
          </div>
        ) : (
          <label className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400" data-testid="textbook-dropzone">
            {uploading ? (
              <span className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="h-5 w-5 animate-spin" /> {phase === "building" ? "Rebuilding sub-concepts in the book's order…" : `Uploading & indexing ${fileName}…`}</span>
            ) : (
              <span className="flex flex-col items-center gap-2 text-slate-500">
                <Upload className="h-6 w-6" />
                <span className="text-sm font-medium">Click to upload a PDF textbook</span>
                <span className="text-xs">Chapter PDF · max 25MB</span>
              </span>
            )}
            <input type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="textbook-upload-input" />
          </label>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FlagDuplicateModal({ topic, candidates, onClose, onFlagged }) {
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!target) {
      toast.error("Select the topic this duplicates");
      return;
    }
    setSaving(true);
    try {
      await req(`/admin/curriculum/duplicate-flags`, {
        method: "POST",
        body: JSON.stringify({
          type: "topic",
          topic_id: topic.id,
          duplicate_of_topic_id: target,
          note,
        }),
      });
      toast.success("Flagged for admin review");
      onFlagged();
    } catch (e) {
      toast.error(e.message || "Could not flag topic");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="flag-dup-modal">
        <DialogTitle className="text-lg font-semibold flex items-center gap-2">
          <Flag className="h-4 w-4 text-amber-600" /> Flag duplicate topic
        </DialogTitle>
        <p className="text-sm text-slate-600">
          Flag <span className="font-medium">{topic.topic}</span> as a duplicate of another topic
          in the same class &amp; subject. An admin will review and decide. Nothing is deleted now.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">This is a duplicate of</label>
            <select
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="flag-dup-target"
            >
              <option value="">Select the original topic…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.topic}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why do you think these are duplicates?"
              data-testid="flag-dup-note"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !target}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="flag-dup-submit"
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Flag for review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FlagSubconceptModal({ source, topics, onClose, onFlagged }) {
  const { topic, cd } = source;
  const [targetTopicId, setTargetTopicId] = useState("");
  const [targetScId, setTargetScId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const targetTopic = topics.find((t) => t.id === targetTopicId);
  const targetSubs = (targetTopic?.concept_details || []).filter(
    (c) => !(targetTopicId === topic.id && c.id === cd.id)
  );

  const submit = async () => {
    if (!targetTopicId || !targetScId) {
      toast.error("Select the topic and sub-concept this duplicates");
      return;
    }
    setSaving(true);
    try {
      await req(`/admin/curriculum/duplicate-flags`, {
        method: "POST",
        body: JSON.stringify({
          type: "subconcept",
          topic_id: topic.id,
          subconcept_id: cd.id,
          duplicate_of_topic_id: targetTopicId,
          duplicate_of_subconcept_id: targetScId,
          note,
        }),
      });
      toast.success("Sub-concept flagged for admin review");
      onFlagged();
    } catch (e) {
      toast.error(e.message || "Could not flag sub-concept");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="flag-sc-modal">
        <DialogTitle className="text-lg font-semibold flex items-center gap-2">
          <Flag className="h-4 w-4 text-amber-600" /> Flag duplicate sub-concept
        </DialogTitle>
        <p className="text-sm text-slate-600">
          Flag <span className="font-medium">{topic.topic} › {cd.title}</span> as a duplicate of
          another sub-concept in the same class &amp; subject. An admin will review and decide.
          Nothing is deleted now.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Original is in topic</label>
            <select
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
              value={targetTopicId}
              onChange={(e) => { setTargetTopicId(e.target.value); setTargetScId(""); }}
              data-testid="flag-sc-target-topic"
            >
              <option value="">Select the topic…</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>{t.topic}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Original sub-concept</label>
            <select
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
              value={targetScId}
              onChange={(e) => setTargetScId(e.target.value)}
              disabled={!targetTopicId}
              data-testid="flag-sc-target-sc"
            >
              <option value="">{targetTopicId ? "Select the sub-concept…" : "Pick a topic first"}</option>
              {targetSubs.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why do you think these are duplicates?"
              data-testid="flag-sc-note"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !targetTopicId || !targetScId}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="flag-sc-submit"
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Flag for review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubconceptList({ topic, jobs, isContentManager = false, onEdit, onRegen, onDelete, onFlag, onReorder, onAddSub }) {
  const cds = topic.concept_details || [];
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const ids = cds.map((c) => c.id);
    const [moved] = ids.splice(dragIndex, 1);
    ids.splice(dropIndex, 0, moved);
    setDragIndex(null);
    setOverIndex(null);
    onReorder(ids);
  };

  const AddBtn = (
    <Button size="sm" variant="outline" onClick={onAddSub} data-testid={`add-sub-${topic.id}`}>
      <Plus className="h-3.5 w-3.5 mr-1" /> Add sub-topic
    </Button>
  );

  if (!cds.length)
    return (
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-slate-500">
          This topic has no sub-concepts yet.
        </p>
        {AddBtn}
      </div>
    );
  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-slate-400">
          Sub-concepts <span className="normal-case font-normal">· drag to reorder</span>
        </p>
        {AddBtn}
      </div>
      {cds.map((cd, index) => {
        const regenning = jobs[`regen:${topic.id}:${cd.id}`]?.status === "running";
        return (
          <div
            key={cd.id}
            className={`border rounded-md p-3 bg-slate-50 transition-colors ${
              overIndex === index ? "border-indigo-400 bg-indigo-50" : ""
            } ${dragIndex === index ? "opacity-50" : ""}`}
            data-testid={`subconcept-${cd.id}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIndex(index);
            }}
            onDrop={() => handleDrop(index)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <span
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                  className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 pt-0.5 shrink-0"
                  data-testid={`drag-sc-${cd.id}`}
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{cd.title}</p>
                  {cd.objective && (
                    <p className="text-xs text-slate-500 mt-0.5">{cd.objective}</p>
                  )}
                  {cd.static_lesson ? (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                      {cd.static_lesson}
                    </p>
                  ) : (
                    <Badge variant="outline" className="mt-1 text-amber-600">
                      No static lesson
                    </Badge>
                  )}
                  {cd.quick_check?.question && (
                    <p className="text-xs text-indigo-600 mt-1">
                      Quick-check: {cd.quick_check.question} →{" "}
                      <span className="font-medium">{cd.quick_check.answer}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(cd)} data-testid={`edit-sc-${cd.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRegen(cd)}
                    disabled={regenning}
                    data-testid={`regen-sc-${cd.id}`}
                  >
                    {regenning ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  {isContentManager ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-amber-600 hover:text-amber-700"
                      onClick={() => onFlag(cd)}
                      data-testid={`flag-sc-${cd.id}`}
                      title="Flag this sub-concept as a duplicate for admin review"
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => onDelete(cd)}
                      data-testid={`del-sc-${cd.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AddSubtopicModal({ topic, model, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [generate, setGenerate] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      toast.error("Enter a sub-topic title");
      return;
    }
    setSaving(true);
    try {
      await req(`/admin/curriculum/subconcept/add`, {
        method: "POST",
        body: JSON.stringify({
          topic_id: topic.id,
          title: title.trim(),
          objective: objective.trim(),
          generate,
          model,
        }),
      });
      toast.success(
        generate ? "Sub-topic added with AI-generated lesson" : "Sub-topic added"
      );
      onSaved();
    } catch (e) {
      toast.error(e.message || "Could not add sub-topic");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="add-sub-modal">
        <h3 className="text-lg font-semibold">Add sub-topic to "{topic.topic}"</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Sub-topic title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="add-sub-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">What should this lesson cover? (guides the AI)</label>
            <Textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={3}
              placeholder="e.g. Define a cube and a cuboid, their faces/edges/vertices, with a real-life example of each. Keep it distinct from 'Finding Volume by Counting Unit Cubes'."
              data-testid="add-sub-objective-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              If a matching section exists in the uploaded book it's used; otherwise the AI follows exactly what you write here.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={generate}
              onChange={(e) => setGenerate(e.target.checked)}
              data-testid="add-sub-generate-checkbox"
            />
            Auto-generate the static lesson &amp; quick-check with AI
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="add-sub-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuestionList({ topicId, model, onEditQ }) {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await req(`/admin/curriculum/topic/${topicId}/questions`);
      setQuestions(data.questions || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e.message || "Could not load questions");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !questions.length) load();
  };

  const regenQ = async (q) => {
    setBusy(q.id);
    try {
      const data = await req(`/admin/curriculum/question/${q.id}/regenerate?model=${encodeURIComponent(model || "")}`, { method: "POST" });
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, ...data.question } : x)));
      toast.success("Question regenerated");
    } catch (e) {
      toast.error(e.message || "Regeneration failed");
    } finally {
      setBusy(null);
    }
  };

  const delQ = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await req(`/admin/curriculum/question/${q.id}`, { method: "DELETE" });
      setQuestions((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Question deleted");
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  return (
    <div className="border-t pt-3">
      <button
        className="flex items-center gap-2 text-sm font-semibold text-slate-700"
        onClick={toggle}
        data-testid={`toggle-questions-${topicId}`}
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        Practice questions
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="flex gap-2 text-xs">
                {["easy", "medium", "hard"].map((d) => (
                  <Badge key={d} className={DIFF_COLORS[d]}>
                    {d}: {counts[d] || 0}
                  </Badge>
                ))}
              </div>
              {questions.length === 0 ? (
                <p className="text-xs text-slate-500">No questions yet.</p>
              ) : (
                questions.map((q) => (
                  <div
                    key={q.id}
                    className="border rounded-md p-2 flex items-start justify-between gap-2 bg-white"
                    data-testid={`question-${q.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={DIFF_COLORS[(q.difficulty || "").toLowerCase()] || ""}>
                          {q.difficulty}
                        </Badge>
                        <span className="text-xs text-slate-400">{q.qtype}</span>
                      </div>
                      <p className="text-sm mt-1">{q.stem}</p>
                      {optionsToArray(q.options).length > 0 && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {optionsToArray(q.options).join(" · ")}
                        </p>
                      )}
                      <p className="text-xs text-emerald-700 mt-0.5">Answer: {q.answer}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => onEditQ(q)} data-testid={`edit-q-${q.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => regenQ(q)}
                        disabled={busy === q.id}
                        data-testid={`regen-q-${q.id}`}
                      >
                        {busy === q.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => delQ(q)}
                        data-testid={`del-q-${q.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RenameTopicModal({ topic, onClose, onSaved }) {
  const [title, setTitle] = useState(topic.topic || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const t = title.trim();
    if (!t) return toast.error("Title cannot be empty");
    setSaving(true);
    try {
      await req(`/admin/curriculum/topic/${topic.id}/rename`, {
        method: "PUT",
        body: JSON.stringify({ title: t }),
      });
      toast.success("Topic renamed");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="rename-topic-modal">
        <DialogTitle>Rename topic</DialogTitle>
        <div className="space-y-2 pt-1">
          <label className="text-xs font-semibold text-slate-500">Topic name</label>
          <Input
            value={title}
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            data-testid="rename-topic-input"
          />
          <p className="text-xs text-slate-400">Changes the display name only. Lessons, questions and student progress stay linked.</p>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rename-topic-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubconceptEditModal({ data, onClose, onSaved }) {
  const { topic, cd } = data;
  const [title, setTitle] = useState(cd.title || "");
  const [lesson, setLesson] = useState(cd.static_lesson || "");
  const [objective, setObjective] = useState(cd.objective || "");
  const [qcQ, setQcQ] = useState(cd.quick_check?.question || "");
  const [qcA, setQcA] = useState(cd.quick_check?.answer || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await req(`/admin/curriculum/subconcept`, {
        method: "PUT",
        body: JSON.stringify({
          topic_id: topic.id,
          subconcept_id: cd.id,
          title,
          static_lesson: lesson,
          objective,
          quick_check: { ...(cd.quick_check || {}), question: qcQ, answer: qcA },
        }),
      });
      toast.success("Sub-concept saved");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-sc-modal">
        <h3 className="text-lg font-semibold">Edit: {cd.title}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Sub-concept name</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sc-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Objective</label>
            <Input value={objective} onChange={(e) => setObjective(e.target.value)} data-testid="sc-objective-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Static lesson</label>
            <Textarea
              value={lesson}
              onChange={(e) => setLesson(e.target.value)}
              rows={8}
              data-testid="sc-lesson-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Quick-check question</label>
              <Textarea value={qcQ} onChange={(e) => setQcQ(e.target.value)} rows={3} data-testid="sc-qc-question-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Quick-check answer</label>
              <Input value={qcA} onChange={(e) => setQcA(e.target.value)} data-testid="sc-qc-answer-input" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="sc-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuestionEditModal({ question, onClose, onSaved }) {
  const [q, setQ] = useState({
    stem: question.stem || "",
    options: optionsToArray(question.options).join("\n"),
    answer: question.answer || "",
    explanation: question.explanation || "",
    hint: question.hint || "",
    difficulty: question.difficulty || "medium",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const options = q.options.split("\n").map((o) => o.trim()).filter(Boolean);
      await req(`/admin/curriculum/question/${question.id}`, {
        method: "PUT",
        body: JSON.stringify({
          stem: q.stem,
          options,
          answer: q.answer,
          acceptable_answers: [q.answer],
          explanation: q.explanation,
          hint: q.hint,
          difficulty: q.difficulty,
        }),
      });
      toast.success("Question saved");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-q-modal">
        <h3 className="text-lg font-semibold">Edit question</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Stem</label>
            <Textarea value={q.stem} onChange={(e) => setQ({ ...q, stem: e.target.value })} rows={3} data-testid="q-stem-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Options (one per line, MCQ)</label>
            <Textarea value={q.options} onChange={(e) => setQ({ ...q, options: e.target.value })} rows={4} data-testid="q-options-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Answer</label>
              <Input value={q.answer} onChange={(e) => setQ({ ...q, answer: e.target.value })} data-testid="q-answer-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Difficulty</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3"
                value={q.difficulty}
                onChange={(e) => setQ({ ...q, difficulty: e.target.value })}
                data-testid="q-difficulty-select"
              >
                <option value="easy">easy</option>
                <option value="medium">medium</option>
                <option value="hard">hard</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Explanation</label>
            <Input value={q.explanation} onChange={(e) => setQ({ ...q, explanation: e.target.value })} data-testid="q-explanation-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Hint</label>
            <Input value={q.hint} onChange={(e) => setQ({ ...q, hint: e.target.value })} data-testid="q-hint-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="q-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
